/**
 * Componente Badge
 *
 * Etiqueta visual para categorías, estados y capas.
 */

import type { ReactNode } from 'react';
import { cn } from '@/core/utils';
import type { Layer, Size } from '@/core/types';
import { getLayerColors, getLayerConfig } from '@/core/constants';

type BadgeVariant = 'default' | 'outline' | 'subtle' | Layer;

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: Size;
  icon?: ReactNode;
  className?: string;
}

const sizeStyles: Record<Size, string> = {
  xs: 'px-1.5 py-0.5 text-2xs gap-0.5',
  sm: 'px-2 py-0.5 text-xs gap-1',
  md: 'px-2.5 py-1 text-xs gap-1',
  lg: 'px-3 py-1 text-sm gap-1.5',
  xl: 'px-3.5 py-1.5 text-sm gap-2',
};

const variantStyles: Record<string, string> = {
  default: 'bg-slate-700 text-slate-200',
  outline: 'bg-transparent border border-slate-600 text-slate-300',
  subtle: 'bg-slate-800/50 text-slate-400',
};

export function Badge({
  children,
  variant = 'default',
  size = 'sm',
  icon,
  className,
}: BadgeProps) {
  // Determinar si es una variante de capa
  const isLayerVariant = ['survival', 'maintenance', 'growth'].includes(variant);

  let variantClasses = variantStyles[variant] ?? variantStyles['default'];

  if (isLayerVariant) {
    const colors = getLayerColors(variant as Layer);
    variantClasses = cn(colors.bgTransparent, colors.text);
  }

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center',
        'font-medium rounded-full',
        'transition-colors duration-200',
        sizeStyles[size],
        variantClasses,
        className
      )}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </span>
  );
}

/**
 * Badge específico para capas
 */
interface LayerBadgeProps {
  layer: Layer;
  size?: Size;
  className?: string;
}

export function LayerBadge({
  layer,
  size = 'sm',
  className,
}: LayerBadgeProps) {
  const config = getLayerConfig(layer);

  return (
    <Badge
      variant={layer}
      size={size}
      {...(className !== undefined && { className })}
    >
      {config.name}
    </Badge>
  );
}
